import { useId, useState } from "react";
import { Button, Icon, Timeline, Typography } from "@/deps/ui-library";
import type { DocumentHistory as History } from "@/api/types";
import { formatDateTime } from "@/lib/dates";

/**
 * Who did what to a document, and when — an RDS `Timeline`.
 *
 * Collapsed by default: on a document that went straight through, the history is four lines nobody
 * needs. On one that was returned twice it is the first thing an auditor opens, so the summary line
 * always says how far it got.
 */
export function DocumentHistory({ history }: { history: History }) {
  const [open, setOpen] = useState(false);
  const listId = useId();

  const steps = [
    { label: "Created", by: history.createdBy, at: history.createdAt },
    { label: "Submitted", by: undefined, at: history.submittedAt },
    { label: "Verified", by: history.verifiedBy, at: history.verifiedAt },
    { label: "Approved", by: history.approvedBy, at: history.approvedAt },
  ].filter((step) => step.at !== undefined);

  const furthest = steps.at(-1);

  return (
    <div className="border-border bg-card rounded-lg border">
      <Button
        variant="ghost"
        colorScheme="default"
        size="sm"
        fullWidth
        rightIcon={open ? "ChevronUp" : "ChevronDown"}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        className="text-muted-foreground justify-between text-xs font-normal"
      >
        <span>
          History
          {furthest ? (
            <span className="ml-1.5">
              · {furthest.label} {formatDateTime(furthest.at)}
            </span>
          ) : null}
        </span>
      </Button>

      {open ? (
        // A list, so a screen reader says "4 items" before reading them: the roles restore what
        // the timeline's divs do not say on their own.
        <Timeline.Root
          id={listId}
          role="list"
          config={{ size: "sm" }}
          className="border-border border-t px-3 pt-3"
        >
          {steps.map((step, index) => (
            <Timeline.Item key={step.label} role="listitem">
              <Timeline.Connector>
                <Timeline.Indicator config={{ size: "sm", variant: "subtle" }} aria-hidden="true">
                  <Icon name="Check" size="xs" />
                </Timeline.Indicator>
                {index < steps.length - 1 ? <Timeline.Separator /> : null}
              </Timeline.Connector>
              <Timeline.Content className="flex-row items-baseline justify-between gap-4">
                <Typography.Text as="span" variant="small">
                  {step.label}
                  {step.by ? <span className="text-muted-foreground"> · {step.by}</span> : null}
                </Typography.Text>
                <Typography.Text as="span" variant="small" muted numeric>
                  {formatDateTime(step.at)}
                </Typography.Text>
              </Timeline.Content>
            </Timeline.Item>
          ))}
        </Timeline.Root>
      ) : null}
    </div>
  );
}
